import React from 'react';
import { mergeProps, useTooltip } from 'react-aria';
import { useMergedRefs } from 'hooks';
import { useTooltip as useTooltipContext } from '../TooltipContext';
import { TooltipPopoverProps } from './types';

function ForwardedTooltipPopover<
  As extends React.ElementType = 'div',
  TTooltip extends HTMLElement = HTMLDivElement,
>(props: TooltipPopoverProps<As>, ref: React.ForwardedRef<TTooltip>) {
  const { as, children, className, style } = props;
  const Component: React.ElementType = as ?? 'div';

  const { attributes, tooltipRef, state, tooltipStyle, tooltipProps } =
    useTooltipContext<TTooltip, HTMLElement>();

  const { tooltipProps: ariaTooltipProps } = useTooltip(tooltipProps, state);

  const mergedRefs = useMergedRefs([tooltipRef, ref]);

  return (
    <Component
      className={className}
      ref={mergedRefs}
      {...mergeProps(props, ariaTooltipProps, attributes)}
      style={{
        ...style,
        ...tooltipStyle,
      }}
    >
      {children}
    </Component>
  );
}

export const TooltipPopover = React.forwardRef(ForwardedTooltipPopover) as <
  As extends React.ElementType = 'div',
  TTooltip extends HTMLElement = HTMLDivElement,
>(
  props: TooltipPopoverProps<As> & { ref?: React.Ref<TTooltip> },
) => React.ReactElement | null;
